/**
 * Origin allowlists for RAM ↔ Cockpit iframe integration.
 * Shared by inbound listeners (`app/embed`) and outbound navigation (`shared/embed`).
 */

/** Inbound navigation + outbound postMessage: trusted Cockpit parent origins. */
export function isAllowedCockpitEmbedParentOrigin(origin: string): boolean {
    if (!origin) {
        return false;
    }
    try {
        const u = new URL(origin);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') {
            return false;
        }
        const host = u.hostname;
        if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
            return true;
        }
        if (host === 'cockpit.local.condo') {
            return true;
        }
        return host === 'condogroup.tech' || host.endsWith('.condogroup.tech');
    } catch {
        return false;
    }
}

/**
 * Stricter allowlist for impersonation injection — only known Cockpit deployment hosts.
 */
export function isAllowedCockpitImpersonationParentOrigin(rawOrigin: string): boolean {
    if (!rawOrigin) {
        return false;
    }
    try {
        const url = new URL(rawOrigin);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return false;
        }
        const host = url.hostname;
        if (['cockpit.local.condo', 'ccs.dev.condogroup.tech', 'ccs.stg.condogroup.tech', 'ccs.condogroup.tech'].includes(host)) {
            return true;
        }
        return /^cockpit(-[A-Za-z0-9-]+)?\.prev\.condogroup\.tech$/.test(host);
    } catch {
        return false;
    }
}

/**
 * Parent origin for outbound `postMessage` when RAM is framed by Cockpit.
 * Uses `document.referrer` (fail closed when stripped or not allowlisted).
 */
export function getCockpitParentPostMessageTargetOrigin(): string | null {
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    if (!referrer) {
        return null;
    }
    try {
        const origin = new URL(referrer).origin;
        return isAllowedCockpitEmbedParentOrigin(origin) ? origin : null;
    } catch {
        return null;
    }
}
